import React, { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../ui/dialog";
import { Button } from "../ui/button";
import { Label } from "../ui/label";
import { Input } from "../ui/input";
import { Switch } from "../ui/switch";
import { Target, Bell } from "lucide-react";
import { toast } from "sonner";

export const TargetDialog = ({ open, onOpenChange, settings, onSave }) => {
  const [type, setType] = useState("sessions");
  const [value, setValue] = useState(14);
  const [reminders, setReminders] = useState(false);

  useEffect(() => {
    if (settings) {
      setType(settings.target_type);
      setValue(settings.target_value);
      setReminders(!!settings.reminders_enabled);
    }
  }, [settings, open]);

  const toggleReminders = async (v) => {
    if (v && "Notification" in window) {
      let perm = Notification.permission;
      if (perm === "default") perm = await Notification.requestPermission();
      if (perm === "denied") {
        toast.error("Allow notifications in your browser to receive reminders");
        setReminders(false);
        return;
      }
    }
    setReminders(v);
  };

  const options = [
    { key: "sessions", label: "Prayer sessions", hint: "Number of prayer sessions to complete this week" },
    { key: "minutes", label: "Total minutes", hint: "Total minutes spent in prayer this week" },
  ];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-2xl border-[#E5E1D8] bg-white p-8 sm:max-w-md" data-testid="target-dialog">
        <DialogHeader>
          <div className="mb-2 flex items-center gap-2 text-brand">
            <Target className="h-5 w-5" />
          </div>
          <DialogTitle className="font-serif text-3xl font-medium text-ink">Set your weekly target</DialogTitle>
          <DialogDescription className="sr-only">Choose to measure your weekly prayer target by sessions or total minutes</DialogDescription>
          <p className="pt-1 font-sans text-sm text-ink-soft">
            A gentle goal to guide your rhythm of prayer. No pressure — just intention.
          </p>
        </DialogHeader>

        <div className="space-y-5 py-2">
          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-[0.1em] text-ink-muted">Measure by</Label>
            <div className="grid grid-cols-2 gap-3">
              {options.map((o) => (
                <button
                  key={o.key}
                  type="button"
                  onClick={() => setType(o.key)}
                  data-testid={`target-type-${o.key}`}
                  className={`rounded-lg border p-3 text-left transition-colors duration-300 ${
                    type === o.key
                      ? "border-brand bg-clay-soft"
                      : "border-[#E5E1D8] bg-white hover:bg-clay-panel"
                  }`}
                >
                  <span className="block font-sans text-sm font-medium text-ink">{o.label}</span>
                </button>
              ))}
            </div>
            <p className="text-xs text-ink-muted">{options.find((o) => o.key === type)?.hint}</p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="target-value" className="text-xs uppercase tracking-[0.1em] text-ink-muted">
              Weekly target ({type === "sessions" ? "sessions" : "minutes"})
            </Label>
            <Input
              id="target-value"
              type="number"
              min={1}
              value={value}
              onChange={(e) => setValue(parseInt(e.target.value || "0", 10))}
              className="rounded-lg border-[#E5E1D8] bg-clay-app focus-visible:ring-[#8FA388]"
              data-testid="target-value-input"
            />
          </div>

          <div className="flex items-center justify-between rounded-lg border border-[#E5E1D8] p-4">
            <div className="flex items-center gap-2">
              <Bell className={`h-4 w-4 ${reminders ? "text-brand" : "text-ink-muted"}`} />
              <div>
                <p className="font-sans text-sm font-medium text-ink">Prayer reminders</p>
                <p className="text-xs text-ink-muted">Gentle nudges at morning, midday & evening (while open)</p>
              </div>
            </div>
            <Switch checked={reminders} onCheckedChange={toggleReminders} data-testid="reminders-switch" />
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            className="rounded-full"
            data-testid="target-cancel-btn"
          >
            Cancel
          </Button>
          <Button
            onClick={() => onSave({ target_type: type, target_value: Math.max(1, value), reminders_enabled: reminders })}
            className="rounded-full bg-brand text-white hover:bg-brand-hover"
            data-testid="target-save-btn"
          >
            Save target
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
